import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { resolveMinNightsForCheckIn, hawaiiTodayYmd, addDaysYmd } from "@shared/promotions";

/**
 * The next few runs of open nights for a home, so a visitor sees a choice
 * instead of finding out a home is taken after picking dates. Only runs long
 * enough for the minimum stay are offered; each links to the booking page
 * with those dates already in place.
 */

type Settings = {
  minNights?: number;
  minNightsSchedule?: { nearMonths: number; nearMinNights: number; farMinNights: number } | null;
};

export { openWindowsFrom, type OpenWindow } from "@shared/open-windows";
import { openWindowsFrom } from "@shared/open-windows";

const shortDay = (ymd: string) =>
  new Date(`${ymd}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

export function useNextOpenWindows(slug?: string | null, horizonDays = 120) {
  const today = hawaiiTodayYmd();
  const until = addDaysYmd(today, horizonDays);
  const { data: availability } = useQuery<{ available_dates: string[] }>({
    queryKey: [`/api/properties/${slug}/availability/merged?start=${today}&end=${until}`],
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
  });
  const { data: settings } = useQuery<Settings>({
    queryKey: [`/api/booking-settings?property=${slug}`],
    enabled: Boolean(slug),
    staleTime: 10 * 60 * 1000,
  });
  return useMemo(() => {
    if (!availability) return null;
    const schedule = settings?.minNightsSchedule;
    const minFor = (checkIn: string) =>
      schedule ? resolveMinNightsForCheckIn(checkIn, schedule, today) : Math.max(1, settings?.minNights ?? 1);
    return openWindowsFrom(availability.available_dates || [], minFor);
  }, [availability, settings, today]);
}

export function NextOpenWindows({ slug, className = "" }: { slug?: string | null; className?: string }) {
  const windows = useNextOpenWindows(slug);
  if (!slug || !windows) return null;
  return (
    <div className={`font-sans text-[12px] text-ink/80 ${className}`} data-testid={`next-open-${slug}`}>
      {windows.length === 0 ? (
        <span>Fully booked for the next four months.</span>
      ) : (
        <>
          <span className="uppercase tracking-[0.16em] text-[10px] text-driftwood">Next open</span>
          <span className="ml-2 inline-flex flex-wrap justify-center gap-x-3 gap-y-1">
            {windows.map((w) => (
              <Link
                key={w.start}
                href={`/properties/${slug}/book?start=${w.start}&end=${w.end}`}
                className="inline-flex min-h-[24px] items-center underline decoration-sand-deep underline-offset-4 hover:text-ink"
                data-testid={`next-open-${slug}-${w.start}`}
              >
                {shortDay(w.start)} to {shortDay(w.end)}{w.nights >= 14 ? " or longer" : ""}
              </Link>
            ))}
          </span>
        </>
      )}
    </div>
  );
}
